import { TranslatedEmb, VisionStep, VisionStepPerimeter, VisionStepPerimeterKey} from '@wizeoo/akm-message-lib';
import { WizMathUtils } from '@wizeoo/akm-core-lib';
import { BaselineEngine, IVo, VisionVO, entity, field } from '@wizeoo/akm-compute-lib';
import { TranslatableLabelVO } from '../common/translation/translatable-label.vo';
import { Model } from '../../model/model';

@entity('wizeoo.allkeyme.vision.message.VisionStepPerimeter', 'VISION')
export class VisionStepPerimeterVO implements IVo {
  constructor(pSource?: VisionStepPerimeter) {
    this._source = null;
    if (pSource) {
      this._source = pSource;
    } else {
      this._source = new VisionStepPerimeter();
      this._source.uid = WizMathUtils.createUUID(); // génération du UID si nouveau
    }
  }

  /** L'objet source provenant du back */
  private _source: VisionStepPerimeter;
  public get source(): VisionStepPerimeter {
    return this._source;
  }
  public set source(value: VisionStepPerimeter) {
    this._source = value;
  }

  @field('string')
  public get code(): string {
    return this._source.code;
  }

  public set code(pValue: string) {
    this._source.code = pValue;
  }

  @field('string')
  public get businessUnitCode(): string {
    // code de la BU
    return this._source.businessUnitCode;
  }
  public set businessUnitCode(value: string) {
    this._source.businessUnitCode = value;
  }

  // Id technique
  public get id(): number {
    return this._source.id;
  }
  public set id(value: number) {
    this._source.id = value;
  }

  // UID technique
  public get uid(): string {
    return this._source.uid;
  }
  public set uid(value: string) {
    this._source.uid = value;
  }

  // code domaine (root), technique
  public get domain(): string {
    return this._source.domain;
  }
  public set domain(value: string) {
    this._source.domain = value;
  }

  /** id de la vision pour manipulation directe */
  public get vid(): number {
    return this._source.vid;
  }
  public set vid(value: number) {
    this._source.vid = value;
  }


  @field('visionCode')
  public get visionCode(): string {
    // code de la vision
    return this._source.visionCode;
  }
  public set visionCode(value: string) {
    this._source.visionCode = value;
  }

  @field('visionCode')
  public get visionStepLevel(): number {
    // code de la vision
    return this._source.visionStepLevel;
  }
  public set visionStepLevel(value: number) {
    this._source.visionStepLevel = value;
  }

  @field('TranslatedEmb')
  public set label(value: TranslatedEmb) {
    this._source.label = value;
  }
  public get label(): TranslatedEmb {
    return this._source.label;
  }

  @field('TranslatedEmb')
  public set description(value: TranslatedEmb) {
    this._source.description = value;
  }
  public get description(): TranslatedEmb {
    return this._source.description;
  }

  @field('string')
  public get chronoCode(): string {
    // code de la BU
    return this._source.chronoCode;
  }
  public set chronoCode(value: string) {
    this._source.chronoCode = value;
  }

  public get version(): number {
    // technique
    return this._source.version;
  }
  public set version(value: number) {
    this._source.version = value;
  }

  // TODO : détecter les changements sur les sous-collections
  @field('maintenanceFlag') // patch pour s'assurer de la demande de mise à jour sur contenu de steps
  public get maintenanceFlag(): string {
    return this._source.maintenanceFlag;
  }
  public set maintenanceFlag(value: string) {
    this._source.maintenanceFlag = value;
  }

  public get keys(): VisionStepPerimeterKey[] {
    return this._source.keys;
  }
  public set keys(value: VisionStepPerimeterKey[]) {
    this._source.keys = value;
  }

  /* ################################################
                    Accesseurs composites
  ################################################ */

  public loaded: boolean = false; // le détail des clés de construction est chargé...

  public get exist(): boolean {
    // l'objet existe en BDD (id)
    return this.id > 0;
  }

  /** Libellé traductible */
  private _labelVO: TranslatableLabelVO;
  public get labelVO(): TranslatableLabelVO {
    if (!this._labelVO) {
      this._labelVO = Model.getInstance().localeEngine.newTranslatableVO(this, 'label');
    }
    return this._labelVO;
  }

  /** description en mode VO */
  private _descriptionVO: TranslatableLabelVO;
  public get descriptionVO(): TranslatableLabelVO {
    if (!this._descriptionVO) {
      this._descriptionVO = Model.getInstance().localeEngine.newTranslatableVO(this, 'description');
    }
    return this._descriptionVO;
  }

  public get trsLabel(): string {
    return Model.getInstance().localeEngine.translate(this.label, this.code);
  }

  public get trsDescription(): string {
    return Model.getInstance().localeEngine.translate(this.description);
  }

  /**
   * Chaine string affichant le level et le label
   */
  public get levelAndLabel(): string {
    return this.visionStepLevel + ' - ' + this.trsLabel;
  }

  /** accesseur sur le visionVO */
  public get visionVO(): VisionVO {
    return Model.getInstance().visionEngine.getVision(this.visionCode);
  }

  /** accesseur sur le visionStepVO */
  public get visionStep(): VisionStep {
    let step: VisionStep = BaselineEngine.getVisionStepByLevel(this.visionVO, this.visionStepLevel);
    return step;
  }

  /** map des clés du vsp */
  private _keyMap: Map<string, VisionStepPerimeterKey> = null;
  public get keyMap(): Map<string, VisionStepPerimeterKey> {
    
    if(!this._keyMap) {
      this._keyMap = new Map<string, VisionStepPerimeterKey>();

      if (!!this._source.keys) {
        this._source.keys.forEach((vspk: VisionStepPerimeterKey) => {
          this._keyMap.set(vspk.key, vspk);
        });
      }
    }
    return this._keyMap;
  }

  /** accesseur pour reset de la map sur retour de service */
  public set keyMap(map: Map<string, VisionStepPerimeterKey>) {
    this._keyMap = map;
  }

}
